import { computed, inject, Ref } from 'vue'
import { Currency, Products, Stocks, Suggestions, Orderpath } from '@one/types'
import { converters } from '@one/core'
import { priceFormatter } from '~/utils/filters'
import useCurrentInstance from '~/utils/useCurrentInstance'
import ExternalUrl = Products.ExternalUrl
import Warehouse = Stocks.Warehouse
import StockVisibilityMode = Stocks.StockVisibilityMode
import { MappedProductStocks, StockWarehousesMapping } from '~/store/stocks/types'
import { DEFAULT_HIDDEN_PRICE_SYMBOL } from '~/composables/useHidePrices'

const { toPackagingQuantityPrice } = converters

export enum ProductImageSize {
  BIG = 'BIG',
  SMALL_100 = 'SMALL_100',
  SMALL_200 = 'SMALL_200',
  SMALL_300 = 'SMALL_300',
  SMALL_400 = 'SMALL_400',
  TINY_32 = 'TINY_32',
}

interface ProductPrice {
  price: number
  catalogPrice: number
  discount: number
  tax?: number
  timestamp?: string
  isLoading: boolean
  isHiddenPrices?: boolean
  hasPrice: boolean
}

export interface ImageVariant {
  fileName: string
  order: number
  url: string
  alt?: string
}

abstract class Deserializable<T> {
  constructor(json?: T) {
    if (json) {
      Object.assign(this, json)
    }
  }
}

export class ProductStocks extends Deserializable<MappedProductStocks> implements MappedProductStocks {
  overallQuantity!: number
  selectedWarehousesQuantity!: number
  unit!: string
  warehouses!: StockWarehousesMapping
  isLoading!: boolean
  stockVisibilityMode!: StockVisibilityMode
  available!: boolean

  currentWarehouse!: Warehouse | null
  parentWarehouse!: Warehouse | null
  orderQuantityInParentWarehouse!: number
  orderQuantityInSelectedWarehouse!: number

  constructor(json: Partial<ProductStocks>) {
    super(json as any)
  }
}

export class Product extends Deserializable<Products.Product> implements Products.Product {
  additionalTechnicalDescription!: Record<string, string>
  attributes!: Array<Products.Attribute>
  canonicalCategory!: string
  catalogIndex!: string
  categoryPath!: Array<string>
  certificates!: Array<Products.ExternalUrl>
  currency!: Currency
  fullDescription!: string
  id!: string
  labels!: Array<string>
  manufacturer!: Products.Manufacturer
  manufacturerIndex!: string
  measurementUnits!: Products.MeasurementUnits
  name!: string
  netDimension!: Products.NetDimension
  photos!: Array<Products.ExternalUrl>
  photosWithVariants!: Array<Products.PhotoWithVariants>
  productDataSheets!: Array<Products.ExternalUrl>
  promotionsImg!: Array<string>
  relatedProducts!: Array<Products.RelatedProductsDto>
  relationsByAttribute!: Array<Products.RelationsByAttribute>
  seo!: Products.ProductSeo
  slug!: string
  surchargePriceAmount!: number
  brand?: Products.Brand
  characteristics!: Array<Suggestions.CharacteristicValue>
  shippingTime?: string
  shortDescription?: string
  usage?: string
  multivalueAttributes!: Array<Products.MultivalueAttribute>
  // @ts-ignore
  price!: ProductPrice
  stocks!: ProductStocks
  cannotOrderAboveStock!: boolean
  energyClass!: Products.EnergyClassDto
  manufacturerId!: string
  pluginsData!: Record<string, any>
  status!: 'ACTIVE' | 'DRAFT_NEW' | 'DRAFT_SENT' | 'DRAFT_REJECTED'
  videos!: Array<Products.ExternalUrl>

  contentUnitPrice() {
    return (
      priceFormatter(
        parseFloat(
          toPackagingQuantityPrice(this.price.price, this.measurementUnits.packingQuantity),
        ),
        this.currency,
      ) || null
    )
  }

  contentUnitCatalogPrice() {
    return (
      priceFormatter(
        parseFloat(
          toPackagingQuantityPrice(this.price.catalogPrice, this.measurementUnits.packingQuantity),
        ),
        this.currency,
      ) || null
    )
  }

  setCurrency(currency: Currency) {
    this.currency = currency
  }

  setPrice(price: ProductPrice) {
    this.price = price
  }

  setStocks(stocks: ProductStocks) {
    this.stocks = stocks
  }

  formattedCurrentPrice(): string | null {
    if (!this.price) {
      return null
    }
    if (this.price.isHiddenPrices) {
      return DEFAULT_HIDDEN_PRICE_SYMBOL
    }
    return priceFormatter(this.price.price, this.currency) || null
  }

  formattedCatalogPrice(): string | null {
    if (!this.price) {
      return null
    }
    if (this.price.isHiddenPrices) {
      return DEFAULT_HIDDEN_PRICE_SYMBOL
    }
    return priceFormatter(this.price.catalogPrice, this.currency) || null
  }

  hasCharacteristic(characteristic: string): boolean {
    return this.characteristics.some((data: Orderpath.App.Shared.CharacteristicData): boolean => {
      return data.characteristicCode === characteristic
    })
  }

  getCharacteristic(characteristic: string) {
    // @ts-ignore
    return this.characteristics.find(
      ({ characteristicCode }) => characteristicCode === characteristic,
    )
  }

  hasLabel(label: string) {
    return this.labels.includes(label)
  }

  getLabel(label: string) {
    return this.labels.find((item: string) => item === label)
  }

  getPhotosInVariant(size: ProductImageSize): Array<ImageVariant> {
    const isVariant = (variant: ImageVariant | null): variant is ImageVariant => {
      return variant !== null
    }
    return this.photosWithVariants
      .map((variant) => {
        if (variant.variantMap && variant.variantMap[size]) {
          return {
            fileName: variant.fileName,
            order: variant.order,
            url: variant.variantMap[size],
            alt: variant?.alt || this.name,
          }
        }
        return null
      })
      .filter(isVariant) as Array<ImageVariant>
  }

  getPhotoInVariant(index: number, size: ProductImageSize): string {
    return this.photosWithVariants[index]?.variantMap[size]
  }

  getPlaceholderImage(): ExternalUrl {
    return {
      fileName: 'photo',
      url: 'https://static-demo-images.s3-eu-west-1.amazonaws.com/photo_plcs_min.svg',
      order: 1,
      placeholder: true,
    }
  }

  hasPhotos(): boolean {
    return this.photosWithVariants.length > 0
  }

  getOrderUnitDescription(): string | undefined {
    return this.measurementUnits?.orderUnitDescription
  }
}

export function useCurrentProduct(externalProductRef?: Ref<Products.Product | null>) {
  const vm = useCurrentInstance()

  const injectedProduct = externalProductRef || inject<any>('currentProduct')

  const product = computed<Product | null>(() => {
    const prodRaw = injectedProduct?.value !== undefined
      ? injectedProduct.value
      : injectedProduct

    if (!prodRaw || !prodRaw.id) {
      return null
    }

    const productData = new Product(prodRaw)

    const currency = vm.$store.getters['layout/getCurrency']
    const isGrossSelected = vm.$store.getters['layout/isGrossSelected']
    const isHiddenPrices = vm.$store.getters['layout/isHiddenPrices']

    productData.setCurrency(currency)

    const productPrice = vm.$store.getters['products/getProductPrice'](prodRaw.id, isGrossSelected)
    productData.setPrice({
      ...(productPrice || {}),
      hasPrice: productPrice !== null,
      isLoading: vm?.$wait?.is(`product-${prodRaw.id}-price-loading`) || false,
      isHiddenPrices: isHiddenPrices,
    })

    const rawStocks = vm.$store.getters['stocks/getProductStocks'](prodRaw.id) || {}
    const defaultWarehouseId = vm.$store.getters['stocks/getDefaultWarehouseId']
    const currentWarehouse = vm.$store.getters['stocks/getWarehouse'](defaultWarehouseId)
    const parentWarehouse = currentWarehouse ? vm.$store.getters['stocks/getWarehouse'](currentWarehouse.parent) : null
    const defaultWarehouse = vm.$store.getters['stocks/getDefaultWarehouse']

    const orderQuantityInParentWarehouse = vm.$store.getters['stocks/getProductOrderQuantityInWarehouse'](
      prodRaw.id,
      parentWarehouse?.id || defaultWarehouse?.id,
    )

    const orderQuantityInSelectedWarehouse = vm.$store.getters['stocks/getProductOrderQuantityInWarehouse'](
      prodRaw.id,
      defaultWarehouse?.id,
    )

    const stocksObj = new ProductStocks({
      ...rawStocks,
      isLoading: vm?.$wait?.is(`product-${prodRaw.id}-stocks-loading`) || false,
      currentWarehouse,
      parentWarehouse,
      orderQuantityInParentWarehouse,
      orderQuantityInSelectedWarehouse,
    })

    productData.setStocks(stocksObj)

    return productData
  })

  return {
    product,
  }
}
